/** Iter 639 — vendor panel split · page-level wizard state: step resume
 * from the vendor record + invite prefill query (moved verbatim from
 * pages/PartnerProgramPage.jsx). Returns the panel bag. */
import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  ENGAGEMENT_HEADERS,
  ENGAGEMENT_PARAM,
  fetchVendorMe,
  stepFromVendor,
} from "@/features/vendorPanel/helpers";

export function useVendorPanel() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, register } = useAuth();

  // ── Step tracker ─────────────────────────────────────────────
  // 1: apply/register  · 2: KYC upload  · 3: Stripe Connect  · 4: done
  const [step, setStep] = useState(1);

  // Iter-316 §Bug1 — resume the wizard from the vendor record on load so
  // an existing/approved vendor lands on the right step instead of
  // looping on "Join the Partner Program".
  const [resumeDone, setResumeDone] = useState(false);
  const vendorQ = useQuery({
    queryKey: ["partner-vendor-me"],
    enabled: !!user,
    retry: false,
    queryFn: fetchVendorMe,
  });
  useEffect(() => {
    if (resumeDone) return;
    if (!user) {
      setResumeDone(true); // anonymous → step 1 (apply)
      return;
    }
    if (vendorQ.isLoading) return;
    if (!vendorQ.isError) setStep(stepFromVendor(vendorQ.data));
    setResumeDone(true); // non-404 fetch error → stay on step 1
  }, [resumeDone, user, vendorQ.isLoading, vendorQ.isError, vendorQ.data]);

  // ── Invite prefill query (only when token present) ───────────
  const inviteQ = useQuery({
    enabled: Boolean(token),
    retry: false,
    queryKey: ["partner-invite", token],
    queryFn: async () => {
      const { data } = await sharedApi.get(
        `/classifieds/engagement/invites/${token}`,
        { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
      );
      return data;
    },
  });

  const invite = inviteQ.data || null;
  const inviteExpired = invite?.status && invite.status !== "pending";

  return {
    token,
    params,
    nav,
    user,
    register,
    step,
    setStep,
    resumeDone,
    vendorQ,
    inviteQ,
    invite,
    inviteExpired,
  };
}
